import { Device } from '@skoda/k2tools-app-services/lib';
import { DefaultTrackingService, GtmTrackingHandler, TrackingProvider as TrackingProviderBase, TrackingUtils } from '@skoda/k2tools-app-services/lib/tracking';
import { removeNullAttributes } from '../utils/helpers';
import { TrackingAppWebName, TrackingConstants, TrackingMatrix, TrackingObject } from './trackingModels';

export class TrackingProvider {

	private device: Device;
	private trackingProviderBase: TrackingProviderBase<TrackingObject>;

	constructor() {
		this.trackingProviderBase = new TrackingProviderBase(new DefaultTrackingService(), new GtmTrackingHandler());

		this.device = new Device({ xs: 0, sm: 576, md: 992, lg: 1200, xl: 1920 });
		this.device.orientationChangeAddListener(this.trackOrientationChange);
	}

	public trackPageView(importerBid: string, culture: string, country: string = null, infotaiment: string = null, filterModel: string = null, dateOfProduction: Date = null, pageName: string = null): void {
		filterModel = this.getCarModelForTracking(filterModel);
		const attributes = removeNullAttributes<TrackingObject>({
			matrixId: TrackingMatrix.pageView,
			event: 'pageView',
			'appweb.Name': TrackingAppWebName.pageView,
			'importer.BID': importerBid,
			'page.Group': TrackingConstants.pageGroup,
			'page.Name': pageName ?? document.title,
			'page.LanguageVersion': culture,
			'page.Context': TrackingUtils.pageContext(null),
			'page.ResponsiveLayout': this.device.fullName(),
			'page.Orientation': this.device.orientationFullName(),
			'filter.Country': country,
			'filter.Infotainment': infotaiment,
			'sc.FilterModel': filterModel,
			'sc.DateOfProduction': dateOfProduction?.toString()
		});

		this.trackingProviderBase.trackAction(attributes);
	}

	public trackVirtualPageView(importerBid: string, culture: string, pageName: string): void {
		const attributes = removeNullAttributes<TrackingObject>({
			matrixId: TrackingMatrix.pageView,
			event: 'pageView',
			documentPath: `/${pageName}`,
			'appweb.Name': TrackingAppWebName.virtualPageView,
			'importer.BID': importerBid,
			'page.Group': TrackingConstants.pageGroup,
			'page.Name': pageName,
			'page.LanguageVersion': culture,
			'page.Context': TrackingUtils.pageContext(null),
			'page.ResponsiveLayout': this.device.fullName(),
			'page.Orientation': this.device.orientationFullName()
		});

		this.trackingProviderBase.trackAction(attributes);
	}

	public trackOrientationChange = (): void => {
		const attributes = removeNullAttributes<TrackingObject>({
			matrixId: TrackingMatrix.orientationChange,
			event: 'trackEvent',
			eventCategory: 'All pages',
			eventAction: TrackingConstants.actionOrientationChange,
			eventLabel: this.device.orientationFullName(),
			eventNonInteraction: 'true',
			'page.Group': TrackingConstants.pageGroup,
			'page.Context': TrackingUtils.pageContext(null),
			'page.ResponsiveLayout': this.device.fullName(),
			'page.Orientation': this.device.orientationFullName(),
			'appweb.Name': TrackingAppWebName.orientationChange,
		});

		this.trackingProviderBase.trackAction(attributes);
	}

	public trackButtonClick(buttonText: string, clickUrl: string = null, country: string = null, infotaiment: string = null, model: string = null, dateOfProduction: Date = null): void {
		model = this.getCarModelForTracking(model);
		const attributes = removeNullAttributes<TrackingObject>({
			matrixId: TrackingMatrix.buttonClick,
			event: 'trackEvent',
			eventCategory: 'Button click',
			eventAction: TrackingConstants.pageGroup,
			eventLabel: buttonText,
			'page.Group': TrackingConstants.pageGroup,
			'page.Context': TrackingUtils.pageContext(null),
			'appweb.Name': TrackingAppWebName.buttonClick,
			'hit.ClickURL': clickUrl,
			'car.Country': country,
			'car.Infotainment': infotaiment,
			'car.Model': model,
			'sc.DateOfProduction': dateOfProduction?.toString()
		});

		this.trackingProviderBase.trackAction(attributes);
	}

	public trackCtaClick(ctaText: string, ctaType: string, clickUrl: string, country: string = null, infotaiment: string = null, model: string = null, dateOfProduction: Date = null): void {
		model = this.getCarModelForTracking(model);
		const attributes = removeNullAttributes<TrackingObject>({
			matrixId: TrackingMatrix.ctaClick,
			event: 'trackEvent',
			eventCategory: 'CTA',
			eventAction: TrackingConstants.pageGroup,
			eventLabel: ctaText,
			'appweb.Name': TrackingAppWebName.ctaClick,
			'page.Group': TrackingConstants.pageGroup,
			'page.Context': TrackingUtils.pageContext(null),
			'cta.Type': ctaType,
			'hit.ClickURL': clickUrl,
			'car.Country': country,
			'car.Infotainment': infotaiment,
			'car.Model': model,
			'sc.DateOfProduction': dateOfProduction?.toString()
		});

		this.trackingProviderBase.trackAction(attributes);
	}

	public trackTabChange(tabName: string): void {
		const attributes = removeNullAttributes<TrackingObject>({
			matrixId: TrackingMatrix.tabChange,
			event: 'trackEvent',
			eventCategory: 'Tab change',
			eventAction: TrackingConstants.pageGroup,
			eventLabel: tabName,
			'appweb.Name': TrackingAppWebName.tabChange,
			'page.Group': TrackingConstants.pageGroup,
			'page.Context': TrackingUtils.pageContext(null)
		});

		this.trackingProviderBase.trackAction(attributes);
	}

	public trackVideoInteraction(videoName: string, label: string, country: string = null, infotaiment: string = null, model: string = null, dateOfProduction: Date = null): void {
		model = this.getCarModelForTracking(model);
		const attributes = removeNullAttributes<TrackingObject>({
			matrixId: TrackingMatrix.video_interaction,
			event: 'trackEvent',
			eventCategory: 'Video play',
			eventAction: videoName,
			eventLabel: label,
			'appweb.Name': TrackingAppWebName.videoInteraction,
			'page.Group': TrackingConstants.pageGroup,
			'page.Context': TrackingUtils.pageContext(null),
			'car.Country': country,
			'car.Infotainment': infotaiment,
			'car.Model': model,
			'sc.DateOfProduction': dateOfProduction?.toString()
		});

		this.trackingProviderBase.trackAction(attributes);
	}

	public trackVideoReach(videoName: string, percentage: string, country: string = null, infotaiment: string = null, model: string = null, dateOfProduction: Date = null): void {
		model = this.getCarModelForTracking(model);
		const attributes = removeNullAttributes<TrackingObject>({
			matrixId: TrackingMatrix.video_reach,
			event: 'trackEvent',
			eventCategory: 'Video play',
			eventAction: videoName,
			eventLabel: `${percentage}% reached`,
			'appweb.Name': TrackingAppWebName.videoReach,
			'page.Group': TrackingConstants.pageGroup,
			'page.Context': TrackingUtils.pageContext(null),
			'car.Country': country,
			'car.Infotainment': infotaiment,
			'car.Model': model,
			'sc.DateOfProduction': dateOfProduction?.toString()
		});

		this.trackingProviderBase.trackAction(attributes);
	}

	public getCarModelForTracking(model: string): string {
		if (model === 'NV') {
			return 'NW';
		}

		if (model === '4V') {
			return '3V';
		}

		if (model === 'NY') {
			return 'NX';
		}

		return model;
	}
}
